/* eslint-disable @typescript-eslint/no-unused-vars */
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { call, put, SagaReturnType, takeLatest } from "redux-saga/effects";
import { formatError } from "utils/errorHelper";
import api from "_config/api";
import {
  AreaEspecialNoPonto,
  RestricaoAbsoluta,
  VersaoPublicada,
  ZonaNoPonto
} from "../types";

export interface ObterPlanoDiretorNoPontoRequest {
  latitude: number;
  longitude: number;
}

export interface PlanoDiretorNoPonto {
  versao: VersaoPublicada;
  longitude: number;
  latitude: number;
  /** Nula quando o ponto não cai em nenhuma zona urbana da versão publicada. */
  zona?: ZonaNoPonto | null;
  areasEspeciais: AreaEspecialNoPonto[];
  restricoesAbsolutas: RestricaoAbsoluta[];
  foraDeZona: boolean;
  motivoForaDeZona?: string | null;
}

interface State {
  isRequesting: boolean;
  /** O ponto consultado. Guardado à parte para o marcador sobreviver ao load. */
  ponto: ObterPlanoDiretorNoPontoRequest | null;
  successPayload: PlanoDiretorNoPonto | null;
  errors: string[];
}

const initialState: State = {
  isRequesting: false,
  ponto: null,
  successPayload: null,
  errors: []
};

const obterPlanoDiretorNoPontoSlice = createSlice({
  name: "consultaprevia@obterPlanoDiretorNoPonto",
  initialState,
  reducers: {
    obterPlanoDiretorNoPonto: (
      state,
      action: PayloadAction<ObterPlanoDiretorNoPontoRequest>
    ): void => {
      state.isRequesting = true;
      state.ponto = action.payload;
      state.successPayload = null;
      state.errors = [];
    },
    obterPlanoDiretorNoPontoSuccess: (
      state,
      action: PayloadAction<PlanoDiretorNoPonto>
    ): void => {
      state.isRequesting = false;
      state.successPayload = action.payload;
    },
    obterPlanoDiretorNoPontoFailed: (
      state,
      action: PayloadAction<string[]>
    ): void => {
      state.isRequesting = false;
      state.errors = action.payload;
    },
    reset: (state): void => {
      state.isRequesting = false;
      state.ponto = null;
      state.successPayload = null;
      state.errors = [];
    }
  }
});

function* sagaFunction(params: {
  type: string;
  payload: ObterPlanoDiretorNoPontoRequest;
}) {
  try {
    const response: SagaReturnType<typeof api> = yield call(
      api.get,
      `api/v1/cidadao/consulta-previa/ponto`,
      {
        params: {
          longitude: params.payload.longitude,
          latitude: params.payload.latitude
        }
      }
    );

    const { data }: { data: PlanoDiretorNoPonto } = response;

    yield put(obterPlanoDiretorNoPontoSuccess(data));
  } catch (error) {
    yield put(obterPlanoDiretorNoPontoFailed(formatError(error)));
  }
}

export const {
  obterPlanoDiretorNoPonto,
  obterPlanoDiretorNoPontoSuccess,
  obterPlanoDiretorNoPontoFailed,
  reset
} = obterPlanoDiretorNoPontoSlice.actions;
export const { reducer } = obterPlanoDiretorNoPontoSlice;
export const saga = takeLatest(obterPlanoDiretorNoPonto.type, sagaFunction);
